import { useEffect, useRef, useState } from 'react';
import {
  isValidLinkedInProfileUrl,
  normalizeLinkedInProfileUrl,
} from '@/lib/validations/social-profiles';

// The server's verdict (Apply::LinkedinChecksController). `checking` is ours: the request
// is in flight or the debounce has not fired yet.
export type LinkedinCheckStatus =
  | 'idle'
  | 'checking'
  | 'invalid'
  | 'taken'
  | 'pending'
  | 'found'
  | 'not_found'
  | 'failed';

export interface LinkedinCheckState {
  status: LinkedinCheckStatus;
  /** The normalised profile URL the verdict is about (set once the server answered). */
  url: string | null;
  fullName: string | null;
  photoUrl: string | null;
  error: string | null;
}

interface CheckResponse {
  status: Exclude<LinkedinCheckStatus, 'idle' | 'checking'>;
  url: string;
  fullName: string | null;
  photoUrl: string | null;
  error: string | null;
}

const IDLE: LinkedinCheckState = {
  status: 'idle',
  url: null,
  fullName: null,
  photoUrl: null,
  error: null,
};
const CHECKING: LinkedinCheckState = { ...IDLE, status: 'checking' };

// Wait this long after the last keystroke before asking: a pasted URL fires at once,
// one typed by hand only when the person pauses.
const DEBOUNCE_MS = 900;
// While the Apify scrape runs (~15s) the verdict is re-read at this pace…
const POLL_MS = 2000;
// …for at most this long before the field reports a failure the person can retry.
const MAX_POLL_MS = 120_000;
// A slug shorter than this is a URL still being typed, never a real profile.
const MIN_SLUG_LENGTH = 3;

// The slug must also be "finished": the URL has the full https://…/in/<slug> shape and
// the slug has a few characters.
function readyToCheck(url: string): boolean {
  if (!isValidLinkedInProfileUrl(url)) return false;
  const slug = url.match(/\/in\/([^/?#]+)/i)?.[1] ?? '';
  return slug.length >= MIN_SLUG_LENGTH;
}

/**
 * Verifies a LinkedIn profile URL against the server while the person types: debounced,
 * only once the URL looks complete, and polled until the background scrape settles.
 * `checkUrl` null disables the whole thing (editions that do not verify LinkedIn).
 */
export function useLinkedinCheck(
  checkUrl: string | null | undefined,
  rawUrl: string,
): LinkedinCheckState & { retry: () => void } {
  const url = normalizeLinkedInProfileUrl(rawUrl);
  const enabled = Boolean(checkUrl) && readyToCheck(url);
  const [state, setState] = useState<LinkedinCheckState>(IDLE);
  // Bumped by `retry` to re-run the lookup for the same URL after a failure.
  const [attempt, setAttempt] = useState(0);
  const retriedRef = useRef(false);

  useEffect(() => {
    if (!checkUrl || !enabled) {
      setState(IDLE);
      return;
    }

    setState(CHECKING);
    const controller = new AbortController();
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout>;

    const ask = async () => {
      try {
        const response = await fetch(
          `${checkUrl}?url=${encodeURIComponent(url)}`,
          {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            signal: controller.signal,
          },
        );
        if (!response.ok && response.status !== 429)
          throw new Error(`HTTP ${response.status}`);
        const body = (await response.json()) as CheckResponse;
        if (controller.signal.aborted) return;

        if (body.status === 'pending') {
          if (Date.now() - startedAt > MAX_POLL_MS) {
            setState({
              ...IDLE,
              status: 'failed',
              error:
                'La verificación está tardando demasiado. Intenta de nuevo en un momento.',
            });
            return;
          }
          setState({ ...IDLE, status: 'pending' });
          timer = setTimeout(ask, POLL_MS);
          return;
        }

        setState({
          status: body.status,
          url: body.url,
          fullName: body.fullName,
          photoUrl: body.photoUrl,
          error: body.error,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('[linkedin-check]', error);
        setState({
          ...IDLE,
          status: 'failed',
          error: 'No pudimos verificar tu perfil. Intenta de nuevo.',
        });
      }
    };

    // A retry asks at once; anything else (every keystroke) waits out the debounce, since
    // each distinct URL that reaches the server is a paid scrape.
    const immediate = retriedRef.current;
    retriedRef.current = false;
    timer = setTimeout(ask, immediate ? 0 : DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [checkUrl, enabled, url, attempt]);

  const retry = () => {
    retriedRef.current = true;
    setAttempt((n) => n + 1);
  };

  return { ...state, retry };
}
